import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { UserName } from "@/components/UserName";
import { Loader2 } from "lucide-react";

const WORDS = {
  no_build_sheet: { title: "No build sheet available", ask: "Why will this truck never have a build sheet?", gap: "no build sheet" },
  vin_unobtainable: { title: "Full VIN can't be obtained", ask: "Why can't the full VIN be obtained?", gap: "VIN not complete" },
};

/* A person's mark, with a reason — or taking it off again. `target` = { row, mark, cell }. */
export function MarkGapDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [target]);
  if (!target) return null;
  const { row, mark, cell } = target;
  const w = WORDS[mark];
  const existing = cell.state === "marked";

  const save = async () => {
    setBusy(true);
    try {
      let next;
      if (existing) {
        await api.delete(`/catch-up/checklist/trucks/${row.id}/mark/${mark}`);
        next = { state: "gap", words: w.gap, link: cell.link, can_mark: mark };
      } else {
        const { data } = await api.post(`/catch-up/checklist/trucks/${row.id}/mark/${mark}`, { reason: reason.trim() });
        next = { state: "marked", words: `${w.title.toLowerCase()} — ${data.reason}`, link: cell.link, mark, by: data.by, at: data.at };
      }
      toast.success(existing ? "Mark taken off — it's a gap again" : "Marked — logged with your name");
      onDone(next);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent data-testid="mark-gap-dialog" className="rounded-xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{row.stock_number} · {w.title}</DialogTitle>
          <DialogDescription>
            {existing ? <>Marked: <b>{cell.words}</b>{cell.by && <> — <UserName email={cell.by} inline /></>}{cell.at ? `, ${new Date(cell.at).toLocaleDateString()}` : ""}.</> : w.ask}
          </DialogDescription>
        </DialogHeader>
        {!existing && (
          <Textarea data-testid="mark-gap-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
            placeholder="e.g. dealer has no record of this unit" className="rounded-lg min-h-[88px]" />
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-lg h-11" data-testid="mark-gap-cancel" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="mark-gap-save" onClick={save} disabled={busy || (!existing && reason.trim().length < 3)}
            className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] font-bold">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{existing ? "Take the mark off" : "Save the mark"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
